/** @file Effective parent linear part for each inherit mode (position always uses the full parent). */
import { trig } from '@pose2d/math/trig';
import type { Affine } from '@pose2d/math/types';
import { assertNever } from '@pose2d/std/assertNever';
import type { InheritMode } from '../skeletonData';

/** An axis this short counts as degenerate (spec §6.2): the parent rotation is read from the other axis. */
const DEGENERATE = 1e-9;

/**
 * Writes E for modes 2-4 into out[0..3]. θp is the angle of the parent's X axis (research §1.3, the documented
 * choice); a degenerate X axis falls back to the Y axis turned by -90°, and both degenerate gives θp = 0.
 * cos θp and sin θp are read straight off the axis (no atan2 round trip), so the golden frames stay exact.
 */
function stripped(out: Affine, pa: number, pb: number, pc: number, pd: number, inherit: 2 | 3 | 4): Affine {
  let c = 1;
  let s = 0;
  const sx = trig.hypot(pa, pc);
  const sy = trig.hypot(pb, pd);
  if (sx > DEGENERATE) {
    c = pa / sx;
    s = pc / sx;
  } else if (sy > DEGENERATE) {
    c = pd / sy;
    s = -pb / sy;
  }
  const reflected = pa * pd - pb * pc < 0;
  if (inherit === 2) {
    // noRotationOrReflection: E = R(-θp)·P₂ = [s, k; 0, det/s], second row negated when the parent is reflected.
    const f = reflected ? -1 : 1;
    out[0] = c * pa + s * pc;
    out[1] = c * pb + s * pd;
    out[2] = f * (c * pc - s * pa);
    out[3] = f * (c * pd - s * pb);
    return out;
  }
  // noScale: R(θp)·diag(1, sign det) keeps a flip; noScaleOrReflection: R(θp) turns a flip into a rotation.
  const f = inherit === 3 && reflected ? -1 : 1;
  out[0] = c;
  out[1] = -s * f;
  out[2] = s;
  out[3] = c * f;
  return out;
}

/**
 * Writes the parent matrix with rotation/scale/reflection stripped according to inherit into out: out[0..3] = E
 * (0 normal P₂, 1 onlyTranslation I, 2 noRotationOrReflection, 3 noScale, 4 noScaleOrReflection), out[4..5] = the
 * parent's origin. Alias-safe (out may be parent); a code outside 0-4 throws (programming error).
 */
export function effectiveParent(out: Affine, parent: Affine, inherit: InheritMode): Affine {
  const pa = parent[0] ?? 0;
  const pb = parent[1] ?? 0;
  const pc = parent[2] ?? 0;
  const pd = parent[3] ?? 0;
  out[4] = parent[4] ?? 0;
  out[5] = parent[5] ?? 0;
  switch (inherit) {
    case 0:
      out[0] = pa;
      out[1] = pb;
      out[2] = pc;
      out[3] = pd;
      return out;
    case 1:
      out[0] = 1;
      out[1] = 0;
      out[2] = 0;
      out[3] = 1;
      return out;
    case 2:
    case 3:
    case 4:
      return stripped(out, pa, pb, pc, pd, inherit);
    default:
      return assertNever(inherit);
  }
}
